import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Bitcoin, ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function ClaimPrize() {
  const { token } = useParams();
  const [claim, setClaim] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    api.get(`/claim/${token}`)
      .then((r) => { setClaim(r.data); setStarted(r.data.claim_status === "verification_started"); })
      .catch((e) => setError(e.response?.status === 404 ? "This claim link is invalid." : "Could not load your claim."))
      .finally(() => setLoading(false));
  }, [token]);

  const start = () => {
    setStarting(true);
    api.post(`/claim/${token}/start`)
      .then(() => setStarted(true))
      .catch((e) => setError(e.response?.data?.detail || "Could not start verification."))
      .finally(() => setStarting(false));
  };

  return (
    <div className="min-h-screen bg-race-bg text-race-txt flex items-center justify-center px-4 py-12" data-testid="claim-page">
      <div className="w-full max-w-md bg-race-surface border border-race-line rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-race-line">
          <Bitcoin size={20} className="text-[#F7931A]" />
          <span className="font-heading text-xl tracking-tight">Claim your Bitcoin prize</span>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-race-sub"><Loader2 className="animate-spin" size={20} /></div>
        ) : error ? (
          <div className="p-6 flex items-start gap-2 text-race-sub" data-testid="claim-error"><AlertTriangle size={18} className="text-race-red mt-0.5" /> {error}</div>
        ) : claim?.expired ? (
          <div className="p-6 flex items-start gap-2 text-race-sub" data-testid="claim-expired"><AlertTriangle size={18} className="text-amber-400 mt-0.5" /> This claim link has expired. Please contact support to have it re-issued.</div>
        ) : (
          <div className="p-6 space-y-5">
            <div>
              <div className="text-sm text-race-sub">Congratulations{claim.winner_name ? `, ${claim.winner_name}` : ""} - you won</div>
              <div className="mt-1 font-heading text-4xl text-[#F7931A]" data-testid="claim-amount">${Number(claim.usd).toLocaleString()}</div>
              {claim.btc > 0 && <div className="text-sm text-race-sub font-mono">≈ {claim.btc} BTC</div>}
              <div className="text-xs text-race-muted mt-1">in {claim.contest_name}</div>
              <div className="text-[11px] text-race-muted mt-1" data-testid="claim-rate-note">
                {claim.rate_locked
                  ? `Rate locked at publish${claim.btc_rate_usd ? ` (1 BTC = $${Number(claim.btc_rate_usd).toLocaleString()})` : ""} - this exact BTC amount is guaranteed.`
                  : "Rate not locked - the exact BTC is set at the live rate when you complete your claim."}
              </div>
            </div>

            <div className="bg-race-bg border border-race-line rounded-md p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm"><ShieldCheck size={16} className="text-race-volt" /> Verify with <strong className="text-race-txt">{claim.partner}</strong></div>
              <p className="text-xs text-race-sub">For your security, identity verification and the Bitcoin payout are handled by our Web3 partner. This page is your secure bridge to their verification.</p>
            </div>

            {started ? (
              <div className="flex items-start gap-2 text-sm text-race-volt" data-testid="claim-started">
                <CheckCircle2 size={18} className="mt-0.5" />
                <span>Verification started with {claim.partner}. You'll be guided through identity checks and payout in their secure webview.
                  <span className="block text-race-muted text-xs mt-1">Demonstration flow - no cryptocurrency is moved in this preview.</span>
                </span>
              </div>
            ) : (
              <button onClick={start} disabled={starting} data-testid="claim-start-btn"
                className="w-full bg-[#F7931A] hover:brightness-110 disabled:opacity-50 text-race-bg font-semibold rounded-sm py-3 flex items-center justify-center gap-2 transition-all">
                {starting ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
                {starting ? "Opening…" : `Verify with ${claim.partner}`}
              </button>
            )}
            <p className="text-[11px] text-race-muted text-center">IBRA &middot; Text Racing</p>
          </div>
        )}
      </div>
    </div>
  );
}
